
import { Part, AliExpressLinks } from "@/data/parts";
import { Button } from "@/components/ui/button";
import PartRating from "@/components/PartRating";
import PartSpecifications from "@/components/PartSpecifications";
import { Cog, Settings, Printer, DollarSign, Wrench, ExternalLink, Cable } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface PartDetailsSidebarProps {
  part: Part;
}

const PartDetailsSidebar = ({ part }: PartDetailsSidebarProps) => {
  // Check if aliExpressLink is an object with multiple links
  const hasMultipleAliExpressLinks = part.aliExpressLink && typeof part.aliExpressLink === 'object';

  return (
    <div className="bg-card rounded-lg border p-6 sticky top-20">
      {/* Image at the top of the sidebar */}
      {part.imageUrl && (
        <div className="mb-6">
          <img 
            src={part.imageUrl} 
            alt={part.name} 
            className="w-full rounded-lg object-cover max-h-[400px]" 
          />
        </div>
      )}
      
      <h2 className="text-lg font-semibold mb-4">Part Information</h2>
      
      <div className="space-y-4">
        {part.rating > 0 && (
          <PartRating rating={part.rating} />
        )}
        
        <div className="flex flex-wrap gap-3 pt-2">
          {part.requiresRoot && (
            <div className="flex items-center gap-1 text-red-500">
              <Settings size={16} />
              <span className="text-xs">Root required</span>
            </div>
          )}
          {!part.requiresRoot && (
            <div className="flex items-center gap-1 text-gray-400">
              <Cog size={16} />
              <span className="text-xs">No root needed</span>
            </div>
          )}
          {part.requiresWiring && (
            <div className="flex items-center gap-1 text-yellow-400">
              <Cable size={16} />
              <span className="text-xs">Requires wiring</span>
            </div>
          )}
          {part.requiresPrinting && (
            <div className="flex items-center gap-1 text-blue-500">
              <Printer size={16} />
              <span className="text-xs">Requires printing</span>
            </div>
          )}
          {part.requiresTools && (
            <div className="flex items-center gap-1 text-purple-600">
              <Wrench size={16} />
              <span className="text-xs">Requires tools</span>
            </div>
          )}
          {part.requiresPurchase && (
            <div className="flex items-center gap-1 text-lime-600 dark:text-green-400">
              <DollarSign size={16} />
              <span className="text-xs">Requires purchase</span>
            </div>
          )}
        </div>
        
        {/* Part Specifications */}
        <div className="pt-4 mb-6">
          <PartSpecifications 
            category={part.category} 
            subcategory={part.subcategory} 
          />
        </div>
        
        <div className="pt-4 space-y-3">
          {part.amazonLink && (
            <Button variant="outline" size="sm" className="w-full dark:border-gray-500 dark:hover:border-gray-300" asChild>
              <a href={part.amazonLink} target="_blank" rel="noopener noreferrer">
                Buy on Amazon
              </a>
            </Button>
          )}
          
          {part.aliExpressLink && !hasMultipleAliExpressLinks && (
            <Button variant="outline" size="sm" className="w-full dark:border-gray-500 dark:hover:border-gray-300" asChild>
              <a href={part.aliExpressLink as string} target="_blank" rel="noopener noreferrer">
                Buy on AliExpress
              </a>
            </Button>
          )}
          
          {hasMultipleAliExpressLinks && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="w-full dark:border-gray-500 dark:hover:border-gray-300">
                  Buy on AliExpress
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Choose Printer</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  {(part.aliExpressLink as AliExpressLinks).global && (
                    <Button variant="outline" className="w-full justify-between dark:border-gray-500 dark:hover:border-gray-300" asChild>
                      <a href={(part.aliExpressLink as AliExpressLinks).global} target="_blank" rel="noopener noreferrer">
                        K1/K1C/K1SE <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  {(part.aliExpressLink as AliExpressLinks).us && (
                    <Button variant="outline" className="w-full justify-between dark:border-gray-500 dark:hover:border-gray-300" asChild>
                      <a href={(part.aliExpressLink as AliExpressLinks).us} target="_blank" rel="noopener noreferrer">
                        K1 Max <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          {part.videoGuide && (
            <Button variant="outline" size="sm" className="w-full dark:border-gray-500 dark:hover:border-gray-300" asChild>
              <a href={part.videoGuide} target="_blank" rel="noopener noreferrer">
                Video Guide
              </a>
            </Button>
          )}
          
          {part.writtenGuide && (
            <Button variant="outline" size="sm" className="w-full dark:border-gray-500 dark:hover:border-gray-300" asChild>
              <a href={part.writtenGuide} target="_blank" rel="noopener noreferrer">
                Written Guide
              </a>
            </Button>
          )}
          
          {part.printedParts && (
            <Button variant="default" size="sm" className="w-full text-white dark:bg-gray-800 dark:hover:bg-gray-700" asChild>
              <a href={part.printedParts} target="_blank" rel="noopener noreferrer">
                Download Printed Parts
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartDetailsSidebar;
